import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  Input,
  Form,
  Select,
  message,
} from '@surecompanies/core_components'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { ReasonCategories } from 'modules/activity-log-service'

const { TextArea } = Input

/**
 * Modal for generating a new VCN with optional activity log form.
 * Used in the legacy Our People section.
 */
const GenerateVcnModalLegacy = ({
  open,
  onCancel,
  onConfirm,
  loading,
  useActivityLogPopup,
  showTerminateWarning,
  personFullName,
}) => {
  const [form] = Form.useForm()
  const [reason, setReason] = useState('')
  const [reasonCategory, setReasonCategory] = useState(undefined)
  const [zendeskTicket, setZendeskTicket] = useState('')

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setReason('')
      setReasonCategory(undefined)
      setZendeskTicket('')
      form.resetFields()
    }
  }, [open, form])

  const handleConfirm = async () => {
    if (useActivityLogPopup) {
      try {
        await form.validateFields()
        onConfirm({
          reason,
          reasonCategory,
          zendeskTicket: zendeskTicket || undefined,
        })
      } catch {
        message.error('Please fill in all required fields')
      }
    } else {
      onConfirm({})
    }
  }

  const title = 'Generate new virtual card'

  // Warning message for existing card termination (only when terminate-all is enabled)
  const warningMessage = showTerminateWarning
    ? `The current card and any unused cards for ${personFullName || 'this employee'} will be terminated immediately. This action can't be undone.`
    : null

  // Reusable warning banner component
  const TerminateWarningBanner = () =>
    warningMessage ? (
      <div
        style={{
          backgroundColor: '#fef0f0',
          border: '1px solid #ffc2c2',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 4,
          }}
        >
          <ExclamationCircleOutlined
            style={{ fontSize: 16, color: '#e3000e' }}
          />
          <span
            style={{
              fontWeight: 500,
              fontSize: 14,
              lineHeight: '18px',
              color: '#e3000e',
            }}
          >
            Current card will be terminated
          </span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ width: 16, flexShrink: 0 }} />
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: '18px',
              color: '#111726',
              fontWeight: 400,
            }}
          >
            {warningMessage}
          </p>
        </div>
      </div>
    ) : null

  // Simple confirmation modal (no activity log)
  if (!useActivityLogPopup) {
    return (
      <Modal
        title={title}
        open={open}
        onCancel={onCancel}
        onOk={handleConfirm}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={loading}
        width={showTerminateWarning ? 520 : 480}
        centered
        destroyOnClose
        maskClosable={true}
        getContainer={false}
      >
        <TerminateWarningBanner />
        <p style={{ margin: 0, color: '#576983' }}>
          Are you sure you want to generate a new virtual card?
        </p>
      </Modal>
    )
  }

  // Activity log modal (with reason form)
  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="Submit"
      cancelText="Cancel"
      confirmLoading={loading}
      okButtonProps={{
        disabled: !reasonCategory || !reason.trim() || loading,
      }}
      width={600}
      centered
      destroyOnClose
      maskClosable={true}
      getContainer={false}
    >
      <TerminateWarningBanner />

      <Form form={form} layout="vertical">
        <Form.Item
          name="reasonCategory"
          label={
            <span>
              Reason Category <span style={{ color: '#ff4d4f' }}>*</span>
            </span>
          }
          rules={[
            {
              required: true,
              message: 'Please select a reason category',
            },
          ]}
          style={{ marginBottom: 16 }}
        >
          <Select
            placeholder="Select"
            value={reasonCategory}
            onChange={setReasonCategory}
            options={ReasonCategories}
            disabled={loading}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label={
            <span>
              Reason <span style={{ color: '#ff4d4f' }}>*</span>
            </span>
          }
          rules={[
            { required: true, message: 'Please enter a reason' },
            {
              min: 3,
              message: 'Reason must be at least 3 characters',
            },
          ]}
          style={{ marginBottom: 16 }}
        >
          <TextArea
            placeholder="Enter the reason for this activity log"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="zendeskTicket"
          label="Zendesk Ticket"
          style={{ marginBottom: 0 }}
        >
          <Input
            placeholder="Enter Zendesk ticket number"
            value={zendeskTicket}
            onChange={(e) => setZendeskTicket(e.target.value)}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

GenerateVcnModalLegacy.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  useActivityLogPopup: PropTypes.bool,
  showTerminateWarning: PropTypes.bool,
  personFullName: PropTypes.string,
}

GenerateVcnModalLegacy.defaultProps = {
  loading: false,
  useActivityLogPopup: false,
  showTerminateWarning: false,
  personFullName: '',
}

export default GenerateVcnModalLegacy
